import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projects } from '../data/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const ProjectsPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState(projects);
  useScrollAnimation();

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam === 'luxury') {
      setSelectedFilter('luxury-kitchen');
    } else if (categoryParam === 'smart') {
      setSelectedFilter('smart-kitchen');
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedFilter === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === selectedFilter));
    }
  }, [selectedFilter]);

  const filters = [
    { label: 'All Projects', value: 'all' },
    { label: 'Luxury Kitchens', value: 'luxury-kitchen' },
    { label: 'Smart Budget Kitchens', value: 'smart-kitchen' },
    { label: 'Wardrobes', value: 'wardrobe' },
    { label: 'Storage Solutions', value: 'storage' }
  ];

  return (
    <div className="bg-[#F4EFE8] min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] lg:h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 parallax-section">
          <img
            src={projects[0].image}
            alt="Projects"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#111111]/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Our Projects
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            A showcase of excellence in modular kitchen and wardrobe design.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 md:py-12 bg-white md:sticky md:top-24 lg:top-28 z-30 shadow-luxury">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="filter-pills-container overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex md:flex-wrap gap-3 md:gap-4 md:justify-center min-w-max md:min-w-0">
              {filters.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setSelectedFilter(filter.value)}
                  className={`flex-shrink-0 px-5 md:px-6 py-2.5 md:py-3 text-xs sm:text-sm tracking-wider uppercase transition-all duration-300 rounded-luxury whitespace-nowrap ${
                    selectedFilter === filter.value
                      ? 'bg-[#111111] text-[#B89A4F]'
                      : 'bg-[#F4EFE8] text-[#111111] hover:bg-[#111111] hover:text-[#B89A4F]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div key={project.id} className="group relative overflow-hidden bg-white shadow-luxury hover:shadow-luxury-hover transition-all duration-500 rounded-luxury">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6 lg:p-8">
                  <div className="text-xs tracking-widest uppercase text-[#B89A4F] mb-2">
                    {project.type}
                  </div>
                  <h3 className="text-2xl font-serif text-[#111111] mb-3">{project.title}</h3>
                  <p className="text-sm text-[#242424] mb-4 leading-relaxed">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.materials.map((material, idx) => (
                      <span key={idx} className="text-xs bg-[#F4EFE8] text-[#242424] px-3 py-1 rounded-full">
                        {material}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-[#242424]">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
